import React from 'react';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { Button, Typography, Box, Link } from '@mui/material';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebaseConfig';

const ProtectedRoute = ({
  user,
  userData,
  children,
  darkMode,         // Currently unused, but kept for consistency
  allowUnapproved,  // If true, user doesn't need to be approved
  devOnly           // If true, user must be a developer
}) => {
  // 1) If no user is logged in, redirect to home
  if (!user) return <Navigate to="/" />;

  // 2) If user’s email is not verified, show a prompt to verify
  if (!user.emailVerified) {
    return (
      <Box
        sx={{
          p: 2,
          textAlign: 'center',
          minHeight: '65vh',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Verification Email Sent, Please Check Your Inbox.
        </Typography>
        <Typography variant="h6" gutterBottom>
          Please verify your email to access this page. You may have to refresh the page.
        </Typography>
        <Button
          variant="contained"
          onClick={async () => {
            try {
              await sendEmailVerification(user);
            } catch (error) {
              console.error('Error sending verification email:', error);
            }
          }}
        >
          Resend Verification Email
        </Button>
      </Box>
    );
  }

  // 3) If userData is still loading or null, show a loading message
  if (!userData) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center',
          minHeight: '65vh',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }

  // 4) If user is not approved and page doesn't allow unapproved access, block them
  if (!allowUnapproved && !userData.approved) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center',
          minHeight: '65vh',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h6">
          You are not subscribed to MicGro Manager, please visit the{' '}
          <Link component={RouterLink} to="/account">
            account
          </Link>{' '}
          page to remedy this.
        </Typography>
      </Box>
    );
  }

  // 5) If this route is dev-only, ensure userData.isDeveloper is true
  if (devOnly && !userData.isDeveloper) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center',
          minHeight: '65vh',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h4" gutterBottom>
          You do not have permission to view this page.
        </Typography>
      </Box>
    );
  }

  // 6) All checks passed—render the protected content
  return children;
};

export default ProtectedRoute;
